/**
 * Content API Layer — MangoTV
 * Pattern: mirrors glotv fetchMoviesApi.js + channels.js
 *
 * All content data flows:
 *   1. fetchAllContent()   — packages → categories → VOD content (home/movies)
 *   2. fetchMovieDetails() — single movie/series details
 *   3. fetchSeriesInfo()   — series metadata + episodes
 *   4. fetchSimilarContent() — related movies/series
 *   5. fetchChannels()     — live TV channels
 *   6. searchContent()     — search movies/series by keyword
 *   7. fetchVideoUrl()     — get playback URL (movie, episode, live)
 *   8. fetchWatchlist()    — user's continue watching / bookmarks
 *   9. fetchPodcasts()    — series filtered by metadata.movie_type === "podcast"
 *
 * Uses apiClient from apiConfig.js (handles auth + dynamic base URL).
 * Falls back to plain axios for some endpoints.
 */

import axios from "axios";
import { getClientApiUrl, fetchAndCacheLandingPage, getCachedLandingPage } from "../config/apiConfig";
import { DEFAULT_OPERATOR } from "../../utils/operators";
import store from "./store";
import { sendLog } from "../utils/sendLog";
import {
  fetchContentBegin,
  fetchContentSuccess,
  fetchContentError,
  fetchMovieDetailsBegin,
  fetchMovieDetailsSuccess,
  fetchMovieDetailsError,
  setSeriesInfo,
  setEpisodes,
  setSimilarMovies,
  fetchChannelsBegin,
  fetchChannelsSuccess,
  fetchChannelsError,
  setChannelCategories,
  setSearchBegin,
  setSearchResults,
  setSearchError,
  fetchVideoBegin,
  fetchVideoSuccess,
  fetchVideoError,
  setBanners,
  setTvStoreCategories,
  setPodcasts,
  setLiveMovies,
  fetchLiveMoviesBegin,
  setWatchlist,
} from "./slice/contentSlice";

// ===== HELPERS =====

const getOperatorUid = () => {
  try {
    const stored = localStorage.getItem("mango_selected_operator");
    if (stored) return JSON.parse(stored).operator_uid;
  } catch {
    // ignore
  }
  return DEFAULT_OPERATOR.operator_uid;
};

const getUserId = () => localStorage.getItem("mango_user_id");
const getAccessToken = () => localStorage.getItem("mango_access_token");

/**
 * Build image URL from the TVAnywhere image system.
 *
 * GloTV pattern:
 *   ${getClientApiUrl()}/api/client/v1/global/images/${imageId}?accessKey=WkVjNWNscFhORDBLCg==
 *
 * Image ID sources (in priority order):
 *   1. content.image_id — primary image for VOD movies
 *   2. content.image_store_id — alternative (series/genre pages)
 *   3. content.images.POSTER — specific poster image
 *   4. content.images.BANNER — banner image
 */
const IMAGE_ACCESS_KEY = "WkVjNWNscFhORDBLCg==";

export const getImageUrl = (content, type = "POSTER") => {
  if (!content) return null;

  // Direct full URL (some API responses include full URLs)
  if (content.poster && content.poster.startsWith("http")) return content.poster;
  if (content.image && content.image.startsWith("http")) return content.image;
  if (content.thumbnail && content.thumbnail.startsWith("http")) return content.thumbnail;

  // Extract image ID from content object
  let imageId = null;

  // Channels use image_stores array (GloTV pattern: image_stores[0].id)
  if (content.image_stores && content.image_stores.length > 0) {
    imageId = content.image_stores[0].id;
  } else if (type === "BANNER") {
    // For banners, prefer BANNER image, fall back to POSTER
    imageId =
      content.images?.BANNER ||
      content.images?.POSTER ||
      content.image_id ||
      content.image_store_id;
  } else {
    // For posters (default), use standard priority
    imageId =
      content.image_id ||
      content.image_store_id ||
      content.images?.POSTER ||
      content.images?.BANNER;
  }

  if (imageId) {
    // GloTV pattern: /api/client/v1/global/images/{id}?accessKey=...
    const baseUrl = getClientApiUrl();
    return `${baseUrl}/api/client/v1/global/images/${imageId}?accessKey=${IMAGE_ACCESS_KEY}`;
  }

  return null;
};

// ===== 1. FETCH ALL CONTENT (Home Page / Movies Page) =====

/**
 * Main content fetch — mirrors GloTV's fetchMovie().
 * 3-step flow: packages → categories → content per category.
 *
 * Dispatches dynamic category arrays to Redux (e.g., state.trending = [...]).
 */
export const fetchAllContent = async () => {
  const dispatch = store.dispatch;
  dispatch(fetchContentBegin());

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    if (!accessToken || !userId) {
      dispatch(fetchContentError("Not authenticated"));
      return false;
    }

    // Step 1: Get user packages
    const packagesRes = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/packages?device_class=desktop`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (packagesRes.data.status !== "ok") {
      dispatch(fetchContentError("Failed to load packages"));
      return false;
    }

    const packageIds = packagesRes.data.data.map((p) => p.id).join(",");

    // Step 2: Get VOD categories
    const categoriesRes = await axios.get(
      `${getClientApiUrl()}/api/client/v3/${operatorUid}/categories/vod?packages=${packageIds}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    console.log("[fetchAllContent] VOD categories raw response:", categoriesRes.data);
    console.log(
      "[fetchAllContent] VOD categories summary:",
      (categoriesRes.data?.data || []).map((c) => ({
        id: c.id,
        uid: c.uid,
        name: c.name,
        branded: c.branded,
        logo_image_id: c.logo_image_id,
        heading_image_id: c.heading_image_id,
        background_image_id: c.background_image_id,
        background_color: c.background_color,
        movies_count: c.movies_count,
        series_count: c.series_count,
      }))
    );

    if (categoriesRes.data.status !== "ok") {
      dispatch(fetchContentError("Failed to load categories"));
      return false;
    }

    const rawCategories = categoriesRes.data.data;
    const categoryMap = {}; // uid -> id
    const categoryIdToUidMap = {}; // id -> uid
    rawCategories.forEach((cat) => {
      categoryMap[cat.uid] = cat.id;
      categoryIdToUidMap[cat.id] = cat.uid;
    });

    const categoryIds = rawCategories.map((c) => c.id).join(",");

    // Step 3: Get content for all categories
    const contentRes = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/categories/vod/content?packages=${packageIds}&categories=${categoryIds}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (contentRes.data.status !== "ok") {
      dispatch(fetchContentError("Failed to load content"));
      return false;
    }

    // Build dynamic categorized content
    const moviesByCategories = {};
    const dynamicCategories = {};

    // Initialize empty arrays for all categories
    rawCategories.forEach((cat) => {
      const key = cat.uid.toLowerCase();
      moviesByCategories[key] = [];
      dynamicCategories[key] = [];
    });

    // Fill categories with content from API response
    // API returns: [{id: categoryId, content: [movies...]}, ...]
    contentRes.data.data.forEach((group) => {
      const uid = categoryIdToUidMap[group.id];
      if (uid && group.content) {
        const key = uid.toLowerCase();
        moviesByCategories[key] = group.content;
        dynamicCategories[key] = group.content;
      }
    });

    // Dispatch everything to Redux
    dispatch(
      fetchContentSuccess({
        categories: rawCategories,
        categoryMap,
        categoryIdToUidMap,
        moviesByCategories,
        packages: packagesRes.data.data,
        packageIds,
        ...dynamicCategories, // spread so state.trending = [...], state.recentlyadded = [...], etc.
      })
    );

    return true;
  } catch (error) {
    console.error("fetchAllContent error:", error.message);
    dispatch(fetchContentError(error.message));
    return false;
  }
};

// ===== 2. FETCH MOVIE DETAILS =====

/**
 * Fetch details for a single movie.
 * Endpoint: /api/client/v1/{operatorUid}/users/{userId}/movies/{movieId}
 */
export const fetchMovieDetail = async (movieId) => {
  const dispatch = store.dispatch;
  dispatch(fetchMovieDetailsBegin());

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/movies/${movieId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok") {
      dispatch(fetchMovieDetailsSuccess(response.data.data));
      return response.data.data;
    }

    dispatch(fetchMovieDetailsError("Movie not found"));
    return null;
  } catch (error) {
    console.error("fetchMovieDetail error:", error.message);
    dispatch(fetchMovieDetailsError(error.message));
    return null;
  }
};

/**
 * Fetch VOD content for categories listed in the CRM landing page's `dashboard_categories`.
 * Used by HomePage to populate "New episode" section with operator-curated content.
 *
 * Flow:
 *   1. Read dashboard_categories (numeric IDs) from cached landing page, fetching if needed.
 *   2. Fetch user packages (required by /categories/vod/content).
 *   3. GET /categories/vod/content?packages={pkgs}&categories={dedupedDashIds}
 *   4. Return flat array of content items.
 *
 * @returns {Array<Object>} — movies from the dashboard categories (may be empty)
 */
export const fetchDashboardContent = async () => {
  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    if (!accessToken || !userId) return [];

    // 1. Get dashboard_categories from landing page
    let landingPage = getCachedLandingPage();
    if (!landingPage) {
      landingPage = await fetchAndCacheLandingPage(operatorUid);
    }
    const rawIds = (landingPage?.dashboard_categories || []).map(Number).filter(Boolean);
    // Unique IDs for fetching; preserve full raw array to honor duplicates in rendering order
    const uniqueIds = [...new Set(rawIds)];
    if (rawIds.length === 0) {
      console.log("[DashboardContent] No dashboard_categories in landing page");
      return [];
    }
    console.log("[DashboardContent] Dashboard category IDs (raw):", rawIds, "unique:", uniqueIds);

    // 2. Get user packages
    const packagesRes = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/packages?device_class=desktop`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (packagesRes.data.status !== "ok") {
      console.warn("[DashboardContent] Failed to load packages");
      return [];
    }
    const packageIds = packagesRes.data.data.map((p) => p.id).join(",");

    // 3. Fetch VOD content for dashboard categories (using unique IDs for the API)
    const contentRes = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/categories/vod/content?packages=${packageIds}&categories=${uniqueIds.join(",")}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (contentRes.data.status !== "ok") {
      console.warn("[DashboardContent] Failed to load content");
      return [];
    }

    // API shape: [{ id: categoryId, content: [movies...] }, ...]
    const groups = contentRes.data.data || [];

    // Fetch category names from /categories/vod
    let categoryNames = {};
    try {
      const catRes = await axios.get(
        `${getClientApiUrl()}/api/client/v3/${operatorUid}/categories/vod?packages=${packageIds}`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (catRes.data.status === "ok") {
        (catRes.data.data || []).forEach((c) => { categoryNames[c.id] = c.name; });
      }
    } catch {
      // Fallback: no category names
    }

    // Build content map by ID (unique content from API)
    const contentByCategoryId = {};
    groups.forEach((g) => { contentByCategoryId[g.id] = g.content || []; });

    // Return one entry per rawId occurrence — preserves duplicate slots so
    // HomePage can map rawIds[0] → first section, rawIds[1] → second section, etc.
    const result = rawIds.map((id) => ({
      categoryId: id,
      categoryName: categoryNames[id] || `Category ${id}`,
      items: contentByCategoryId[id] || [],
    }));
    console.log("[DashboardContent] Returning", result.length, "slots for raw IDs:", rawIds.join(","));
    return result;
  } catch (error) {
    console.error("[DashboardContent] Error:", error.message);
    return [];
  }
};

// ===== 3. FETCH SERIES INFO =====

/**
 * Fetch series metadata (seasons, episodes).
 * Endpoint: /api/client/v1/{operatorUid}/series/{seriesId}
 */
export const fetchSeriesDetail = async (seriesId) => {
  const dispatch = store.dispatch;

  try {
    const accessToken = getAccessToken();
    const operatorUid = getOperatorUid();

    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/series/${seriesId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok") {
      dispatch(setSeriesInfo(response.data.data));

      // Extract episodes from seasons if available
      const series = response.data.data;
      if (series.seasons && series.seasons.length > 0) {
        // Default to first season's episodes
        const firstSeason = series.seasons[0];
        if (firstSeason.episodes) {
          dispatch(setEpisodes(firstSeason.episodes));
        }
      }

      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error("fetchSeriesDetail error:", error.message);
    return null;
  }
};

/**
 * Fetch episodes for a specific season of a series.
 */
export const fetchSeasonEpisodes = async (seriesId, seasonNumber) => {
  const dispatch = store.dispatch;

  try {
    const accessToken = getAccessToken();
    const operatorUid = getOperatorUid();

    // Try to get from series endpoint and filter by season
    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/series/${seriesId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok") {
      const series = response.data.data;
      const season = series.seasons?.find(
        (s) => s.season_number === seasonNumber || s.number === seasonNumber
      );
      if (season?.episodes) {
        dispatch(setEpisodes(season.episodes));
        return season.episodes;
      }
    }
    return [];
  } catch (error) {
    console.error("fetchSeasonEpisodes error:", error.message);
    return [];
  }
};

// ===== 4. FETCH SIMILAR CONTENT =====

/**
 * Fetch related/similar movies or series.
 * Endpoint: /api/client/v1/{operatorUid}/vod/{type}/{id}/related
 */
export const fetchSimilarContent = async (contentId, type = "movies") => {
  const dispatch = store.dispatch;

  try {
    const accessToken = getAccessToken();
    const operatorUid = getOperatorUid();

    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/vod/${type}/${contentId}/related`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok") {
      dispatch(setSimilarMovies(response.data.data || []));
      return response.data.data;
    }
    dispatch(setSimilarMovies([]));
    return [];
  } catch (error) {
    console.error("fetchSimilarContent error:", error.message);
    dispatch(setSimilarMovies([]));
    return [];
  }
};

// ===== 5. FETCH LIVE TV CHANNELS =====

/**
 * Fetch all live TV channels available to the user.
 * Flow: packages → channels filtered by packages.
 */
export const fetchLiveChannels = async () => {
  const dispatch = store.dispatch;
  dispatch(fetchChannelsBegin());

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    if (!accessToken || !userId) {
      dispatch(fetchChannelsError("Not authenticated"));
      return false;
    }

    // Get user packages first
    const packagesRes = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/packages?device_class=desktop`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (packagesRes.data.status !== "ok") {
      dispatch(fetchChannelsError("Failed to load packages"));
      return false;
    }

    const packageIds = packagesRes.data.data.map((p) => p.id).join(",");

    // Get channels
    const channelsRes = await axios.get(
      `${getClientApiUrl()}/api/client/v2/${operatorUid}/channels?packages=${packageIds}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    console.log("[LiveTV] Channels API response:", channelsRes.data);

    if (channelsRes.data.status === "ok") {
      const channelsList = channelsRes.data.data || [];
      console.log("[LiveTV] Channels list:", channelsList);

      // The v2 channels endpoint doesn't return image_stores.
      // GloTV pattern: fetch individual channel info via v1 to get image_stores.
      // Enrich each channel with its detailed info (parallel requests).
      const enrichedChannels = await Promise.all(
        channelsList.map(async (ch) => {
          try {
            const detailRes = await axios.get(
              `${getClientApiUrl()}/api/client/v1/${operatorUid}/channels/${ch.id}`,
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );
            if (detailRes.data.status === "ok" && detailRes.data.data) {
              console.log("[LiveTV] Channel detail for", ch.id, ":", detailRes.data.data);
              // Merge detailed info (image_stores, etc.) into the channel object
              return { ...ch, ...detailRes.data.data };
            }
          } catch (e) {
            // Individual channel fetch failed — use base data
            console.warn(`Channel detail fetch failed for ${ch.id}:`, e.message);
          }
          return ch;
        })
      );

      console.log("[LiveTV] Enriched channels:", enrichedChannels);
      dispatch(fetchChannelsSuccess(enrichedChannels));
      return true;
    }

    dispatch(fetchChannelsError("Failed to load channels"));
    return false;
  } catch (error) {
    console.error("fetchLiveChannels error:", error.message);
    dispatch(fetchChannelsError(error.message));
    return false;
  }
};

/**
 * Fetch channel categories for live TV filtering.
 */
export const fetchLiveChannelCategories = async () => {
  const dispatch = store.dispatch;

  try {
    const accessToken = getAccessToken();
    const operatorUid = getOperatorUid();

    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/categories/channels`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    console.log("[LiveTV] Channel categories response:", response.data);
    if (response.data.status === "ok") {
      dispatch(setChannelCategories(response.data.data || []));
      return response.data.data;
    }
    return [];
  } catch (error) {
    console.error("fetchLiveChannelCategories error:", error.message);
    return [];
  }
};

/**
 * Fetch individual channel details (image_stores, name, etc.).
 * GloTV pattern: GET /api/client/v1/{operatorUid}/channels/{channelId}
 *
 * @param {string|number} channelId — channel ID
 * @returns {object|null} — channel detail object or null
 */
export const fetchChannelInfo = async (channelId) => {
  if (!channelId) return null;

  try {
    const accessToken = getAccessToken();
    const operatorUid = getOperatorUid();

    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/channels/${channelId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok" && response.data.data) {
      return response.data.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) return null;
    console.warn(`fetchChannelInfo error for ${channelId}:`, error.message);
    return null;
  }
};

/**
 * Fetch EPG (Electronic Program Guide) for channels.
 * Endpoint: /api/client/v2/{operatorUid}/shows/grid?channels={channelIds}
 */
/**
 * Fetch EPG (Electronic Program Guide) for channels.
 * API: GET /v2/{ouid}/shows/grid?channels={ids}&start_date={iso}&end_date={iso}
 *
 * @param {string} channelIds — comma-separated channel IDs
 * @param {Date} [startDate] — show start (default: today)
 * @param {Date} [endDate] — show end (default: start + 1 day)
 */
export const fetchChannelEPG = async (channelIds, startDate, endDate) => {
  if (!channelIds) return null;

  try {
    const accessToken = getAccessToken();
    const operatorUid = getOperatorUid();

    let url = `${getClientApiUrl()}/api/client/v2/${operatorUid}/shows/grid?channels=${channelIds}`;

    if (startDate) {
      url += `&start_date=${startDate.toISOString()}`;
    }
    if (endDate) {
      url += `&end_date=${endDate.toISOString()}`;
    }

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (response.data.status === "ok") {
      return response.data.data;
    }
    return null;
  } catch (error) {
    console.error("fetchChannelEPG error:", error.message);
    return null;
  }
};

// ===== 6. SEARCH CONTENT =====

/**
 * Search movies/series by keyword.
 * Endpoint: /api/client/v1/{operatorUid}/search/movies/{query}?packages={packageIds}
 */
export const searchContent = async (keyword) => {
  const dispatch = store.dispatch;

  if (!keyword || keyword.trim().length === 0) {
    dispatch(setSearchResults({ query: "", results: [] }));
    return [];
  }

  dispatch(setSearchBegin());

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    // Sanitize: remove special chars (keep alphanumeric + spaces)
    // GloTV pattern: keyword.replace(/[^\w\s]/gi, "")
    const sanitized = keyword.replace(/[^\w\s]/gi, "").trim();

    if (!sanitized) {
      dispatch(setSearchResults({ query: keyword, results: [] }));
      return [];
    }

    // Try to reuse package IDs from Redux state
    let packageIds = store.getState().content.packageIds;

    // If no cached packages, fetch them
    if (!packageIds) {
      try {
        const packagesRes = await axios.get(
          `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/packages?device_class=desktop`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (packagesRes.data.status === "ok") {
          packageIds = packagesRes.data.data.map((p) => p.id).join(",");
        }
      } catch {
        packageIds = "";
      }
    }

    // Search — GloTV pattern: /search/movies/{query}?translation=hr&packages={ids}
    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/search/movies/${sanitized}?translation=hr&packages=${packageIds || ""}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok") {
      dispatch(
        setSearchResults({
          query: keyword,
          results: response.data.data || [],
        })
      );
      return response.data.data;
    }

    if (response.data.status === "error") {
      dispatch(setSearchResults({ query: keyword, results: [] }));
      return [];
    }

    dispatch(setSearchResults({ query: keyword, results: [] }));
    return [];
  } catch (error) {
    console.error("searchContent error:", error.message);
    dispatch(setSearchError(error.message));
    return [];
  }
};

// ===== 7. FETCH VIDEO URL =====

/**
 * Fetch playback URL for a movie, episode, or live channel.
 *
 * API paths per type (from OpenAPI spec):
 *   movie:  GET /v1/{ouid}/users/{user_id}/vod/movies/{movie_uid}     — expects UID string
 *   series: GET /v1/{ouid}/users/{user_id}/vod/episodes/{episode_id}  — expects numeric ID
 *   live:   GET /v1/{ouid}/users/{user_id}/live/channels/{channel_uid} — expects UID string
 *
 * @param {string|number} id — content ID or UID
 * @param {"movie"|"series"|"live"} type — content type
 */
export const fetchVideoUrl = async (id, type = "movie") => {
  const dispatch = store.dispatch;
  dispatch(fetchVideoBegin());

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    console.log("[fetchVideoUrl] type:", type, "id:", id);

    let url;
    if (type === "movie") {
      // Spec expects movie_uid (string like "tt1408101_star_trek"), but numeric ID may also work
      url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/vod/movies/${id}`;
    } else if (type === "series") {
      // Spec expects episode_id (numeric)
      url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/vod/episodes/${id}`;
    } else if (type === "live") {
      // Spec expects channel_uid (string like "france24"), NOT numeric channel ID
      url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/live/channels/${id}`;
    }

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    console.log("[fetchVideoUrl] Response status:", response.data.status, "| Full response data:", JSON.stringify(response.data.data, null, 2));

    if (response.data.status === "ok") {
      dispatch(fetchVideoSuccess(response.data.data));

      // Log playout event to CRM (fire-and-forget).
      // Resolve a human-readable title from already-loaded Redux state when possible.
      try {
        const state = store.getState().content || {};
        let contentName = "";
        if (type === "movie") {
          contentName = state.movieDetails?.title || "";
        } else if (type === "series") {
          contentName =
            state.seriesInfo?.title ||
            state.movieDetails?.title ||
            "";
        } else if (type === "live") {
          const channel = (state.channels || []).find(
            (c) => c.uid === id || String(c.id) === String(id)
          );
          contentName = channel?.name || channel?.short_name || "";
        }
        sendLog({
          action: "play",
          content_uid: String(id),
          content_type: type,
          content_name: contentName,
          duration: response.data.data?.duration || 0,
        });
      } catch (logErr) {
        console.warn("[fetchVideoUrl] play-log skipped:", logErr.message);
      }

      return response.data.data;
    }

    console.warn("[fetchVideoUrl] Non-ok status:", response.data);
    dispatch(fetchVideoError(response.data.message || "Failed to get video URL"));
    return null;
  } catch (error) {
    console.error("[fetchVideoUrl] Error:", error.message, error.response?.status, error.response?.data);
    dispatch(fetchVideoError(error.response?.data?.message || error.message));
    return null;
  }
};

// ===== 8. FETCH TRAILER =====

/**
 * Fetch trailer URL for a movie or episode.
 * GloTV pattern: GET /vod/trailers/movies/{id} or /vod/trailers/episodes/{id}
 *
 * @param {string|number} id — content ID
 * @param {"movie"|"series"} type — content type
 * @returns {string|null} — trailer video URL or null
 */
export const fetchTrailer = async (id, type = "movie") => {
  if (!id) return null;

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    const kind = type === "series" ? "episodes" : "movies";
    const url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/vod/trailers/${kind}/${id}`;

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (response.data?.data?.url) {
      return response.data.data.url;
    }
    return null;
  } catch (error) {
    // Trailers may not exist for all content — fail silently
    return null;
  }
};

// ===== 9. FETCH WATCHLIST =====

/**
 * Fetch user's watchlist / bookmarks.
 * Tries my_content endpoint first, falls back to profile bookmarks.
 */
export const fetchUserWatchlist = async () => {
  const dispatch = store.dispatch;

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();
    const profileId = localStorage.getItem("mango_profile_id") || 1;

    // Try my_content endpoint first
    try {
      const url1 = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/my_content?profile_id=${profileId}`;
      const response = await axios.get(url1, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (response.data.status === "ok") {
        const movieBookmarks = (response.data.data?.movie_bookmarks || []).map(b => ({ ...b, _type: "movie" }));
        const episodeBookmarks = (response.data.data?.episode_bookmarks || []).map(b => ({ ...b, _type: "episode" }));
        const combined = [...movieBookmarks, ...episodeBookmarks];
        console.log("[fetchUserWatchlist] my_content: movies:", movieBookmarks.length, "episodes:", episodeBookmarks.length);
        dispatch(setWatchlist(combined));
        return combined;
      }
    } catch {
      // my_content endpoint not available — try fallback
    }

    // Fallback: profile bookmarks endpoint (movies only)
    const url2 = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/profiles/${profileId}/bookmarks/movies`;
    const response = await axios.get(url2, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (response.data.status === "ok") {
      const movieBookmarks = (response.data.data || []).map(b => ({ ...b, _type: "movie" }));
      console.log("[fetchUserWatchlist] fallback: movies:", movieBookmarks.length);
      dispatch(setWatchlist(movieBookmarks));
      return movieBookmarks;
    }

    dispatch(setWatchlist([]));
    return [];
  } catch (error) {
    console.error("fetchUserWatchlist error:", error.message);
    dispatch(setWatchlist([]));
    return [];
  }
};

/**
 * Fetch bookmark (last watch position) for a movie or episode.
 * API: GET /v1/{ouid}/users/{userId}/bookmarks/movies/{movieId}?profile={profileId}
 *
 * @param {string|number} contentId — movie or episode ID (numeric)
 * @param {"movie"|"series"} type — content type
 * @returns {number|null} — time in ms, or null if no bookmark
 */
export const getBookmark = async (contentId, type = "movie") => {
  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();
    const profileId = localStorage.getItem("mango_profile_id") || 1;

    const kind = type === "series" ? "episodes" : "movies";
    const url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/bookmarks/${kind}/${contentId}?profile=${profileId}`;

    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (response.data?.status === "ok" && response.data.data) {
      const time = response.data.data.time || response.data.data.position || 0;
      return time;
    }
    return null;
  } catch (error) {
    // No bookmark exists — not an error
    return null;
  }
};

/**
 * Update watch progress bookmark.
 */
export const updateWatchProgress = async (
  contentId,
  type = "movie",
  timeMs = 0
) => {
  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();
    const profileId = localStorage.getItem("mango_profile_id") || 1;

    let url;
    if (type === "series") {
      url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/bookmarks/episodes/${contentId}?profile=${profileId}`;
    } else {
      url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/bookmarks/movies/${contentId}/${contentId}?profile=${profileId}`;
    }

    await axios.put(
      url,
      { time: timeMs },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    return true;
  } catch (error) {
    console.error("updateWatchProgress error:", error.message);
    return false;
  }
};

// ===== 10. ADD TO WATCHLIST =====

/**
 * Add a movie/series to the user's watchlist (bookmark).
 * GloTV pattern: PUT /bookmarks/movies/{id}/{bookmarkName}?profile={profileId}
 *
 * @param {string|number} contentId — movie or episode ID
 * @param {"movie"|"series"} type — content type
 * @returns {boolean} — true if successful
 */
export const addToWatchlist = async (contentId, type = "movie") => {
  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();
    const profileId = localStorage.getItem("mango_profile_id") || 1;
    const bookmarkName = String(contentId);

    let url;
    if (type === "series") {
      url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/bookmarks/episodes/${contentId}?profile=${profileId}`;
    } else {
      url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/bookmarks/movies/${contentId}/${bookmarkName}?profile=${profileId}`;
    }

    const response = await axios.put(
      url,
      { time: 0, name: bookmarkName },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok") {
      // Refresh watchlist in Redux
      await fetchUserWatchlist();
      return true;
    }
    return false;
  } catch (error) {
    console.error("addToWatchlist error:", error.message);
    return false;
  }
};

// ===== 11. REMOVE FROM WATCHLIST =====

/**
 * Remove a movie/series from the user's watchlist.
 * GloTV pattern: DELETE /bookmarks/movies/{id}?profile={profileId}
 *
 * @param {string|number} contentId — movie or episode ID
 * @param {"movie"|"series"} type — content type
 * @returns {boolean} — true if successful
 */
export const removeFromWatchlist = async (contentId, type = "movie") => {
  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();
    const profileId = localStorage.getItem("mango_profile_id") || 1;

    let url;
    if (type === "series") {
      url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/bookmarks/episodes/${contentId}?profile=${profileId}`;
    } else {
      url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/bookmarks/movies/${contentId}?profile=${profileId}`;
    }

    const response = await axios.delete(url, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (response.data.status === "ok") {
      // Refresh watchlist in Redux
      await fetchUserWatchlist();
      return true;
    }
    return false;
  } catch (error) {
    console.error("removeFromWatchlist error:", error.message);
    return false;
  }
};

// ===== 12. FETCH GENRES =====

/**
 * Fetch all genres for the operator. Cache in module scope.
 * API: GET /v1/{ouid}/genres
 * @returns {Object} — map of genreId → genre name
 */
let cachedGenreMap = null;

export const fetchGenres = async () => {
  if (cachedGenreMap) return cachedGenreMap;

  try {
    const accessToken = getAccessToken();
    const operatorUid = getOperatorUid();

    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/genres`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok" && response.data.data) {
      const map = {};
      response.data.data.forEach((g) => { map[g.id] = g.name; });
      cachedGenreMap = map;
      console.log("[Genres] Loaded", Object.keys(map).length, "genres");
      return map;
    }
    return {};
  } catch (error) {
    console.error("[Genres] fetchGenres error:", error.message);
    return {};
  }
};

// ===== 13. FETCH AGE RATINGS =====

/**
 * Fetch all age ratings for the operator. Cache in module scope.
 * API: GET /v1/{ouid}/age_ratings
 * @returns {Object} — map of ageRatingId → rating label (e.g. "TV-MA")
 */
let cachedAgeRatingMap = null;

export const fetchAgeRatings = async () => {
  if (cachedAgeRatingMap) return cachedAgeRatingMap;

  try {
    const accessToken = getAccessToken();
    const operatorUid = getOperatorUid();

    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/age_ratings`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok" && response.data.data) {
      const map = {};
      response.data.data.forEach((r) => { map[r.id] = r.value; });
      cachedAgeRatingMap = map;
      console.log("[AgeRatings] Loaded", Object.keys(map).length, "ratings");
      return map;
    }
    return {};
  } catch (error) {
    console.error("[AgeRatings] fetchAgeRatings error:", error.message);
    return {};
  }
};

// ===== 14. FETCH MOVIE DETAILS (BATCH — no Redux dispatch) =====

/**
 * Fetch movie details for multiple movies in parallel.
 * Returns enriched movie objects with movie_genres, cast, director, etc.
 * Does NOT dispatch to Redux — for index building only.
 *
 * @param {Array<{id, uid}>} movies — movies with at least id field
 * @returns {Array<Object>} — full movie objects
 */
export const fetchMovieDetailsBatch = async (movies) => {
  if (!movies || movies.length === 0) return [];

  const accessToken = getAccessToken();
  const userId = getUserId();
  const operatorUid = getOperatorUid();

  if (!accessToken || !userId) return [];

  console.log("[MovieDetailsBatch] Fetching details for", movies.length, "movies");

  const results = await Promise.all(
    movies.map(async (movie) => {
      try {
        const response = await axios.get(
          `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/movies/${movie.id}`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (response.data.status === "ok" && response.data.data) {
          return response.data.data;
        }
        return null;
      } catch {
        return null;
      }
    })
  );

  const valid = results.filter(Boolean);
  console.log("[MovieDetailsBatch] Got", valid.length, "details,", valid.filter(m => m.movie_genres?.length > 0).length, "with genres");
  return valid;
};

// ===== 15. FETCH TV STORE CATEGORIES =====

/**
 * Fetch TV Store categories from the Client API.
 * API: GET /v1/{ouid}/categories/tv_store
 *
 * Response includes categories with: id, uid, name, description, branded, heading_image_id,
 * background_image_id, background_color, logo_image_id, tv_store[] (products).
 *
 * Used by: DiscoveryPage (subscription cards), DiscoveryCategoryPage, Brands, Indian Movies
 *
 * @returns {Array} — array of TV Store category objects
 */
export const fetchTvStoreCategories = async () => {
  const dispatch = store.dispatch;

  try {
    const accessToken = getAccessToken();
    const operatorUid = getOperatorUid();

    if (!accessToken) {
      console.log("[TvStoreCategories] Not authenticated, skipping fetch");
      dispatch(setTvStoreCategories([]));
      return [];
    }

    console.log("[TvStoreCategories] Fetching...");

    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/categories/tv_store`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok" && response.data.data) {
      const categories = response.data.data;
      console.log("[TvStoreCategories] Loaded", categories.length, "categories:",
        categories.map((c) => `${c.uid}(branded:${c.branded},products:${c.tv_store?.length || 0})`).join(", "));
      dispatch(setTvStoreCategories(categories));
      return categories;
    }

    console.warn("[TvStoreCategories] Non-ok response:", response.data);
    dispatch(setTvStoreCategories([]));
    return [];
  } catch (error) {
    console.error("[TvStoreCategories] Error:", error.message);
    dispatch(setTvStoreCategories([]));
    return [];
  }
};

// ===== 15b. FETCH BRANDED VOD CATEGORIES =====

/**
 * Fetch VOD categories from the Client API and return only those flagged
 * `branded: true`. These power the HomePage "Brands" rail (e.g. HBO, Disney+).
 *
 * API: GET /v3/{ouid}/categories/vod?packages={packageIds}
 *
 * Each branded category exposes:
 *   - id, uid, name, desc
 *   - branded: true
 *   - background_color  → card background fill
 *   - heading_image_id  → image rendered inside the card (logo/wordmark)
 *   - background_image_id, logo_image_id (extras)
 *
 * @returns {Array<Object>} branded VOD categories (may be empty)
 */
export const fetchBrandedVodCategories = async () => {
  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    if (!accessToken || !userId) {
      console.log("[BrandedVodCategories] Not authenticated, skipping");
      return [];
    }

    // 1. User packages (required by /categories/vod)
    const packagesRes = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/packages?device_class=desktop`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (packagesRes.data.status !== "ok") {
      console.warn("[BrandedVodCategories] Failed to load packages");
      return [];
    }
    const packageIds = packagesRes.data.data.map((p) => p.id).join(",");

    // 2. VOD categories
    const catRes = await axios.get(
      `${getClientApiUrl()}/api/client/v3/${operatorUid}/categories/vod?packages=${packageIds}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (catRes.data.status !== "ok") {
      console.warn("[BrandedVodCategories] Non-ok response");
      return [];
    }

    const all = catRes.data.data || [];
    const branded = all.filter((c) => c.branded === true);
    console.log(
      "[BrandedVodCategories] Found",
      branded.length,
      "branded of",
      all.length,
      ":",
      branded.map((c) => `${c.uid}(bg:${c.background_color},head:${c.heading_image_id})`).join(", ")
    );
    return branded;
  } catch (error) {
    console.error("[BrandedVodCategories] Error:", error.message);
    return [];
  }
};

// ===== 15c. FETCH VOD CATEGORY CONTENT (single category) =====

/**
 * Fetch the movies/series in a single VOD category.
 * Used by BrandPage to populate the poster grid for a branded category.
 *
 * API: GET /v1/{op}/categories/vod/content?packages={ids}&categories={categoryId}
 *
 * @param {number|string} categoryId — VOD category numeric id
 * @returns {Array<Object>} content items (id, uid, title, image_id, type, year, …)
 */
export const fetchVodCategoryContent = async (categoryId) => {
  if (!categoryId) return [];

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    if (!accessToken || !userId) return [];

    const packagesRes = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/packages?device_class=desktop`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (packagesRes.data.status !== "ok") return [];
    const packageIds = packagesRes.data.data.map((p) => p.id).join(",");

    const contentRes = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/categories/vod/content?packages=${packageIds}&categories=${categoryId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (contentRes.data.status !== "ok") return [];

    const group = (contentRes.data.data || []).find((g) => g.id === Number(categoryId));
    const items = group?.content || [];
    console.log("[VodCategoryContent] cat", categoryId, "→", items.length, "items");
    return items;
  } catch (error) {
    console.error("[VodCategoryContent] Error:", error.message);
    return [];
  }
};

// ===== 15. FETCH TV STORE PRODUCT DETAIL =====

/**
 * Fetch a single TV Store product detail.
 * API: GET /v1/{ouid}/tv_store/{tv_store_id}
 *
 * Returns: id, uid, card_image_id, big_image_id, name, description, tv_store_options[]
 *
 * @param {number|string} tvStoreId
 * @returns {Object|null}
 */
export const fetchTvStoreProduct = async (tvStoreId) => {
  if (!tvStoreId) return null;

  try {
    const accessToken = getAccessToken();
    const operatorUid = getOperatorUid();

    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/tv_store/${tvStoreId}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok" && response.data.data) {
      // API returns array with one item
      const product = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
      console.log("[TvStoreProduct] Loaded:", product.uid, product.name, "card_img:", product.card_image_id, "big_img:", product.big_image_id);
      return product;
    }
    return null;
  } catch (error) {
    console.warn("[TvStoreProduct] Error fetching", tvStoreId, ":", error.message);
    return null;
  }
};

/**
 * Uniqcast Uniform error envelope parser.
 * Shape: { code, status: "error", data: { message, details? } }
 */
const parseApiError = (error) => {
  const body = error.response?.data;
  const msg = body?.data?.message || body?.message || error.message;
  return { message: msg, code: body?.code, status: error.response?.status };
};

/**
 * Fetch v2 user tv_store (map format keyed by tv_store id) — needed for package_id resolution.
 * API: GET /v2/{ouid}/users/{user_id}/tv_store
 *
 * @returns {Object|null} Map { "<tv_store_id>": { uid, action, price_options: [{ id, uid, packages: [{ id }] }] } }
 */
export const fetchUserTvStoreV2 = async () => {
  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();
    const res = await axios.get(
      `${getClientApiUrl()}/api/client/v2/${operatorUid}/users/${userId}/tv_store`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (res.data?.status === "ok") {
      return res.data.data || {};
    }
    return null;
  } catch (error) {
    console.warn("[UserTvStoreV2] Error:", parseApiError(error).message);
    return null;
  }
};

/**
 * Resolve the TV Store product ID that grants access to a content's packages.
 *
 * Backend convention (see MovieDetailsPage TV Store match at ~line 163): a content's
 * `packages[].uid` directly matches a TV Store product's top-level `uid`. That's the
 * primary lookup. Falls back to numeric-id matching via user tv_store v2 for endpoints
 * that return packages as bare numeric IDs (e.g. `user_movies_v2`, `user_series`).
 *
 * In either path, the candidate product must have non-empty `price_options` in v1
 * (that's what PickPlanPage reads) — otherwise the pick-plan page renders
 * "No plans available".
 *
 * Accepts BOTH shapes:
 *   - `[{ uid: "foo", id: 1 }, ...]` — full package objects (e.g. fetchMovieDetail)
 *   - `[1, 2, 3]` or `["uid1", "uid2"]` — bare IDs / UIDs
 *
 * @param {Array<number|string|{id?: number, uid?: string}>} packages
 * @returns {Promise<string|null>} — TV Store product id as string, or null
 */
export const resolveProductIdForPackages = async (packages) => {
  if (!Array.isArray(packages) || packages.length === 0) return null;

  // Normalize the input into parallel sets of UIDs and numeric IDs.
  const uids = new Set();
  const ids = new Set();
  packages.forEach((p) => {
    if (p == null) return;
    if (typeof p === "object") {
      if (p.uid) uids.add(String(p.uid));
      if (p.id != null) ids.add(String(p.id));
    } else if (typeof p === "number") {
      ids.add(String(p));
    } else if (typeof p === "string") {
      // Heuristic: all-digits = id, otherwise uid.
      if (/^\d+$/.test(p)) ids.add(p);
      else uids.add(p);
    }
  });

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    const v1Res = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/tv_store`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    const v1List = v1Res.data?.status === "ok" ? (v1Res.data.data || []) : [];

    // ── Primary: match TV Store product.uid against the content's package UIDs.
    //    This is the convention MovieDetailsPage already uses.
    if (uids.size > 0) {
      const matched = v1List.find(
        (p) => uids.has(String(p.uid)) && (p.price_options?.length || 0) > 0
      );
      if (matched) {
        console.log("[resolveProductIdForPackages] matched by uid:", matched.uid, "→ id", matched.id);
        return String(matched.id);
      }
    }

    // ── Fallback: numeric-id match through v2 packages map. For content endpoints
    //    that return `packages: [numeric_id, ...]` (user_movies_v2, user_series).
    //
    // Two-pass: first prefer products that ALSO appear in v1 with price_options
    // (cleanest case — PickPlanPage reads v1). If nothing matches, fall back to
    // any v2 product with price_options. PickPlanPage will then need to resolve
    // those plans from v2 itself.
    if (ids.size > 0) {
      const v2Map = await fetchUserTvStoreV2();
      if (v2Map) {
        const v1ById = {};
        v1List.forEach((p) => { v1ById[String(p.id)] = p; });

        const v2Candidates = [];
        for (const [tvStoreId, product] of Object.entries(v2Map)) {
          const hasPackage = (product.price_options || []).some((po) =>
            (po.packages || []).some((pkg) => ids.has(String(pkg.id)))
          );
          if (!hasPackage) continue;
          if ((product.price_options?.length || 0) === 0) continue;
          v2Candidates.push(tvStoreId);
        }

        // Pass 1: prefer v2 candidate that's also in v1 with price_options.
        for (const tvStoreId of v2Candidates) {
          const v1Prod = v1ById[String(tvStoreId)];
          if (v1Prod && (v1Prod.price_options?.length || 0) > 0) {
            console.log("[resolveProductIdForPackages] matched by id via v2 (v1+v2):", tvStoreId);
            return String(tvStoreId);
          }
        }

        // Pass 2: any v2 candidate with price_options. Covers podcasts and other
        // SVOD products the user already owns (so they don't appear in v1).
        if (v2Candidates.length > 0) {
          console.log("[resolveProductIdForPackages] matched by id via v2 only:", v2Candidates[0]);
          return String(v2Candidates[0]);
        }
      }
    }

    console.warn("[resolveProductIdForPackages] no purchasable product for", { uids: [...uids], ids: [...ids] });
    return null;
  } catch (error) {
    console.warn("[resolveProductIdForPackages] error:", error.message);
    return null;
  }
};

/**
 * Resolve BOTH the TV Store product id AND its rental price for a content's
 * packages. Wraps resolveProductIdForPackages (id resolution) and then reads the
 * rental `activation_price` from the v1 user tv_store (v2 carries no price).
 *
 * Used by SeriesDetailsPage so a rentable series shows "Rent (₦X)" exactly like
 * MovieDetailsPage. A null price means the product is SVOD/subscription-only
 * (no v1 activation price) → caller should render "Subscribe" instead.
 *
 * @param {Array<number|string|{id?: number, uid?: string}>} packages
 * @returns {Promise<{ productId: string|null, price: number|null }>}
 */
export const resolveRentalForPackages = async (packages) => {
  const productId = await resolveProductIdForPackages(packages);
  if (!productId) return { productId: null, price: null };
  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();
    const res = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/tv_store`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    const list = res.data?.status === "ok" ? (res.data.data || []) : [];
    const prod = list.find((p) => String(p.id) === String(productId));
    const price = prod?.price_options?.[0]?.activation_price ?? null;
    console.log("[resolveRentalForPackages] productId:", productId, "price:", price);
    return { productId, price };
  } catch (error) {
    console.warn("[resolveRentalForPackages] price lookup failed:", error.message);
    return { productId, price: null };
  }
};

/**
 * Fetch payment options for a content item (required before v2 purchase).
 * API: GET /v1/{ouid}/users/{user_id}/payment_options/{type}/{content_id}
 * Params: device_class, device_os, package_id
 *
 * @param {string} contentType — "TV_STORE"
 * @param {number|string} contentId
 * @param {number|string} packageId — from price_options[].packages[0].id (v2 user tv_store)
 * @returns {Array<{ id, type, configuration }>|null}
 */
export const fetchPaymentOptions = async (contentType, contentId, packageId) => {
  if (!contentType || !contentId) return null;
  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    const params = new URLSearchParams({ device_class: "DESKTOP", device_os: "WEB" });
    if (packageId) params.set("package_id", String(packageId));

    const url = `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/payment_options/${contentType}/${contentId}?${params}`;
    console.log("[PaymentOptions] GET", { contentType, contentId, packageId });

    const res = await axios.get(url, { headers: { Authorization: `Bearer ${accessToken}` } });
    if (res.data?.status === "ok") {
      const options = Array.isArray(res.data.data) ? res.data.data : [];
      console.log("[PaymentOptions] Got", options.length, "options:", options.map(o => `${o.id}:${o.type}`).join(", "));
      return options;
    }
    return [];
  } catch (error) {
    const { message, code, status } = parseApiError(error);
    console.warn("[PaymentOptions] Error:", { message, code, status });
    return { error: message, code, status };
  }
};

/**
 * Purchase a TV Store product via Client API v2.
 * API: POST /v2/{ouid}/users/{user_id}/purchase
 * Body: { content_type, content_id, package_id, payment_option, payment_option_type, main_pin, purchase_info? }
 *
 * Two-step internally: first fetches payment_options, picks the first one (or BILLING_SYSTEM preferred),
 * then POSTs the purchase. main_pin defaults to "1234" per current product direction (no pin modal).
 *
 * @param {number|string} tvStoreId — content_id
 * @param {string} priceOptionUid — for logging/display only
 * @param {number|string} packageId — required for payment_options lookup + purchase body
 * @returns {{ success: boolean, pending?: boolean, cdrId?: number, error?: string, status?: number }}
 */
export const purchaseTvStore = async (tvStoreId, priceOptionUid, packageId) => {
  if (!tvStoreId || !packageId) {
    return { success: false, error: "Missing tv_store_id or package_id" };
  }

  // 1. Fetch payment options
  const options = await fetchPaymentOptions("TV_STORE", tvStoreId, packageId);

  if (!Array.isArray(options) || options.length === 0) {
    const errorMsg = options?.error
      ? `Payment options unavailable: ${options.error}`
      : "No payment options configured for this product";
    console.warn("[PurchaseTvStore] Cannot proceed —", errorMsg);
    return { success: false, error: errorMsg, status: options?.status };
  }

  // Prefer BILLING_SYSTEM if present, else first option
  const chosen = options.find((o) => o.type === "BILLING_SYSTEM") || options[0];
  console.log("[PurchaseTvStore] Using payment option:", chosen.id, chosen.type);

  // 2. POST v2 purchase
  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    const body = {
      content_type: "TV_STORE",
      content_id: Number(tvStoreId),
      package_id: Number(packageId),
      payment_option: chosen.id,
      payment_option_type: chosen.type,
      main_pin: "1234",
    };
    console.log("[PurchaseTvStore] POST v2 purchase", { tvStoreId, priceOptionUid, packageId, payment_option: chosen.id });

    const res = await axios.post(
      `${getClientApiUrl()}/api/client/v2/${operatorUid}/users/${userId}/purchase`,
      body,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    const status = res.status;
    if (res.data?.status === "ok") {
      console.log("[PurchaseTvStore] Success:", { status, data: res.data.data, pending: status === 202 });
      // Some gateways return a redirect URL for hosted payment pages
      const redirectUrl = res.data.data?.redirect_url || res.data.data?.redirectUrl;
      return { success: true, pending: status === 202, cdrId: res.data.data?.id, redirectUrl, status };
    }
    return { success: false, error: res.data?.message || "Purchase failed", status };
  } catch (error) {
    const { message, code, status } = parseApiError(error);
    console.error("[PurchaseTvStore] Error:", message, "code:", code);
    return { success: false, error: message, status };
  }
};

/**
 * Fetch multiple TV Store product details in parallel.
 * @param {Array<{id, uid}>} productStubs — from category.tv_store[]
 * @returns {Array<Object>} — full product objects with card_image_id, name, etc.
 */
export const fetchTvStoreProducts = async (productStubs) => {
  if (!productStubs || productStubs.length === 0) return [];

  console.log("[TvStoreProducts] Fetching details for", productStubs.length, "products");

  const products = await Promise.all(
    productStubs.map((stub) => fetchTvStoreProduct(stub.id))
  );

  const valid = products.filter(Boolean);
  console.log("[TvStoreProducts] Got", valid.length, "product details");
  return valid;
};

// ===== 16. FETCH USER BANNERS =====

/**
 * Fetch banners for the current user, filtered by device class.
 * API: GET /v1/{ouid}/users/{user_id}/banners?device_class=DESKTOP
 *
 * Banner types: APP | LIVE | TVSTORE | VOD | WEB | CATEGORY
 * - LIVE banners: hero for LiveShowPage, Champions League section
 * - VOD banners: hero for HomePage, DiscoveryPage
 *
 * @param {string} [deviceClass="DESKTOP"]
 * @returns {Array} — array of banner objects
 */
export const fetchUserBanners = async (deviceClass = "DESKTOP") => {
  const dispatch = store.dispatch;

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    if (!accessToken || !userId) {
      console.log("[Banners] Not authenticated, skipping fetch");
      dispatch(setBanners([]));
      return [];
    }

    console.log("[Banners] Fetching user banners, device_class:", deviceClass);

    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/banners?device_class=${deviceClass}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok" && response.data.data) {
      const banners = response.data.data;
      console.log("[Banners] Loaded", banners.length, "banners:", banners.map((b) => `${b.type}:${b.title || b.uid}`).join(", "));
      dispatch(setBanners(banners));
      return banners;
    }

    console.warn("[Banners] Non-ok response:", response.data);
    dispatch(setBanners([]));
    return [];
  } catch (error) {
    console.error("[Banners] fetchUserBanners error:", error.message);
    dispatch(setBanners([]));
    return [];
  }
};

// ===== 13. FETCH EPISODE DETAILS (BATCH) =====

/**
 * Fetch full episode details for multiple episodes.
 * The series endpoint only returns { id, uid, number, title, image_id, duration }.
 * This endpoint returns description, cast, director, audio_languages, subtitle_languages, images, etc.
 *
 * API: GET /v1/{ouid}/episodes?episodes_id=1,2,3
 *
 * @param {number[]} episodeIds — array of episode IDs
 * @returns {Object} — map of episodeId → full episode object
 */
export const fetchEpisodeDetails = async (episodeIds) => {
  if (!episodeIds || episodeIds.length === 0) return {};

  try {
    const accessToken = getAccessToken();
    const operatorUid = getOperatorUid();
    const ids = episodeIds.join(",");

    console.log("[Episodes] Fetching details for", episodeIds.length, "episodes:", ids);

    const response = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/episodes?episodes_id=${ids}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (response.data.status === "ok" && response.data.data) {
      const episodes = response.data.data;
      console.log("[Episodes] Got details for", episodes.length, "episodes");

      // Build map: episodeId → episode object
      const episodeMap = {};
      episodes.forEach((ep) => { episodeMap[ep.id] = ep; });
      return episodeMap;
    }

    console.warn("[Episodes] Non-ok response:", response.data);
    return {};
  } catch (error) {
    console.error("[Episodes] fetchEpisodeDetails error:", error.message);
    return {};
  }
};

// ===== 13. FETCH PODCASTS =====

/**
 * Fetch podcast series — series with metadata.movie_type === "podcast".
 *
 * Flow:
 *   1. Get user packages (reuse cached packageIds if available)
 *   2. Fetch user series with packages → get series IDs
 *   3. Fetch full series details for those IDs → get metadata + images
 *   4. Filter by metadata.movie_type === "podcast"
 *   5. Dispatch to Redux
 *
 * API refs (verified from OpenAPI spec):
 *   GET /v1/{ouid}/users/{user_id}/series?packages={ids}  — user series list
 *   GET /v1/{ouid}/series?series_id={ids}                 — full series details
 */
export const fetchPodcasts = async () => {
  const dispatch = store.dispatch;

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    if (!accessToken || !userId) {
      console.log("[Podcasts] Not authenticated, skipping fetch");
      dispatch(setPodcasts([]));
      return [];
    }

    console.log("[Podcasts] Starting fetch...");

    // Step 1: Get package IDs (reuse from Redux if available)
    let packageIds = store.getState().content.packageIds;
    if (!packageIds) {
      console.log("[Podcasts] No cached packages, fetching...");
      const packagesRes = await axios.get(
        `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/packages?device_class=desktop`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (packagesRes.data.status === "ok") {
        packageIds = packagesRes.data.data.map((p) => p.id).join(",");
      } else {
        console.warn("[Podcasts] Failed to load packages");
        dispatch(setPodcasts([]));
        return [];
      }
    }

    console.log("[Podcasts] Using packageIds:", packageIds);

    // Step 2: Fetch user series (returns IDs + purchase info)
    const userSeriesRes = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/series?packages=${packageIds}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (userSeriesRes.data.status !== "ok" || !userSeriesRes.data.data?.length) {
      console.log("[Podcasts] No series available for user");
      dispatch(setPodcasts([]));
      return [];
    }

    const userSeriesList = userSeriesRes.data.data;
    const seriesIds = userSeriesList.map((s) => s.id).join(",");
    console.log("[Podcasts] User has", userSeriesList.length, "series, IDs:", seriesIds);

    // Step 3: Fetch full series details (metadata, images, title, etc.)
    const seriesDetailRes = await axios.get(
      `${getClientApiUrl()}/api/client/v1/${operatorUid}/series?series_id=${seriesIds}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (seriesDetailRes.data.status !== "ok" || !seriesDetailRes.data.data?.length) {
      console.log("[Podcasts] Failed to load series details");
      dispatch(setPodcasts([]));
      return [];
    }

    const allSeries = seriesDetailRes.data.data;
    console.log("[Podcasts] Fetched details for", allSeries.length, "series");

    // Step 4: Filter for podcasts — metadata.movie_type === "podcast"
    const podcasts = allSeries.filter((s) => {
      const movieType = s.metadata?.movie_type;
      return movieType === "podcast";
    });

    console.log("[Podcasts] Found", podcasts.length, "podcast series out of", allSeries.length, "total");

    // Merge user-specific data (purchased, favorited) into full series objects
    const userSeriesMap = {};
    userSeriesList.forEach((us) => { userSeriesMap[us.id] = us; });

    const enrichedPodcasts = podcasts.map((series) => ({
      ...series,
      purchased: userSeriesMap[series.id]?.purchased || null,
      favorited: userSeriesMap[series.id]?.favorited || false,
      episode_bookmarks: userSeriesMap[series.id]?.episode_bookmarks || [],
    }));

    dispatch(setPodcasts(enrichedPodcasts));
    return enrichedPodcasts;
  } catch (error) {
    console.error("[Podcasts] fetchPodcasts error:", error.message);
    dispatch(setPodcasts([]));
    return [];
  }
};

// ===== FETCH LIVE MOVIES =====

/**
 * Fetch movies tagged as live events — movies with metadata.movie_type === "live".
 * Drives the Upcoming/Past tabs on LiveShowPage. Live channels (real-time TV)
 * are still served by fetchLiveChannels.
 *
 * Flow mirrors fetchPodcasts (movies version):
 *   1. Get user packages (reuse cached packageIds when available)
 *   2. Fetch user movies with packages → IDs + purchase info
 *   3. Fetch full movie details (in parallel) → metadata + release_date
 *   4. Filter by metadata.movie_type === "live"
 *   5. Dispatch to Redux
 *
 * API refs (verified from OpenAPI spec):
 *   GET /v2/{ouid}/users/{user_id}/movies?packages={ids}  — user movies list
 *   GET /v1/{ouid}/users/{user_id}/movies/{movie_id}      — full movie detail
 */
export const fetchLiveMovies = async () => {
  const dispatch = store.dispatch;
  dispatch(fetchLiveMoviesBegin());

  try {
    const accessToken = getAccessToken();
    const userId = getUserId();
    const operatorUid = getOperatorUid();

    if (!accessToken || !userId) {
      console.log("[LiveMovies] Not authenticated, skipping fetch");
      dispatch(setLiveMovies([]));
      return [];
    }

    console.log("[LiveMovies] Starting fetch...");

    // Step 1: Get package IDs (reuse cached when available)
    let packageIds = store.getState().content.packageIds;
    if (!packageIds) {
      console.log("[LiveMovies] No cached packages, fetching...");
      const packagesRes = await axios.get(
        `${getClientApiUrl()}/api/client/v1/${operatorUid}/users/${userId}/packages?device_class=desktop`,
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      if (packagesRes.data.status === "ok") {
        packageIds = packagesRes.data.data.map((p) => p.id).join(",");
      } else {
        console.warn("[LiveMovies] Failed to load packages");
        dispatch(setLiveMovies([]));
        return [];
      }
    }

    console.log("[LiveMovies] Using packageIds:", packageIds);

    // Step 2: Fetch user movies (returns IDs + purchase info)
    const userMoviesRes = await axios.get(
      `${getClientApiUrl()}/api/client/v2/${operatorUid}/users/${userId}/movies?packages=${packageIds}`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (userMoviesRes.data.status !== "ok" || !userMoviesRes.data.data?.length) {
      console.log("[LiveMovies] No movies available for user");
      dispatch(setLiveMovies([]));
      return [];
    }

    const userMoviesList = userMoviesRes.data.data;
    console.log("[LiveMovies] User has", userMoviesList.length, "movies");

    // Step 3: Fetch full movie details (parallel batch — same pattern as fetchMovieDetailsBatch).
    // We can't use a single bulk endpoint for movies the way we can for series, so we batch.
    const movieDetails = await fetchMovieDetailsBatch(userMoviesList);
    console.log("[LiveMovies] Fetched details for", movieDetails.length, "of", userMoviesList.length, "movies");

    // Step 4: Filter for live events — metadata.movie_type === "live"
    const liveMovies = movieDetails.filter((m) => m?.metadata?.movie_type === "live");
    console.log("[LiveMovies] Found", liveMovies.length, "live-event movies out of", movieDetails.length);

    // Merge user-specific data (purchased, favorited) into details
    const userMoviesMap = {};
    userMoviesList.forEach((um) => { userMoviesMap[um.id] = um; });

    const enriched = liveMovies.map((m) => ({
      ...m,
      purchased: userMoviesMap[m.id]?.purchased || null,
      favorited: userMoviesMap[m.id]?.favorited || false,
    }));

    dispatch(setLiveMovies(enriched));
    return enriched;
  } catch (error) {
    console.error("[LiveMovies] fetchLiveMovies error:", error.message);
    dispatch(setLiveMovies([]));
    return [];
  }
};
